import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Megaphone, UserPlus, Pencil, Trash2, RefreshCcw, BarChart3 } from "lucide-react";
import { Modal, Field, FieldSelect, FieldTextarea } from "./AdminBusinesses";

export default function AdminPromoters() {
  const [items, setItems] = useState([]);
  const [perf, setPerf] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get(`/admin/promoters`),
      api.get(`/admin/promoter-performance`),
    ]);
    setItems(a.data.items || []);
    setPerf(b.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const perfFor = (id) => perf.find((p) => p.promoter_id === id) || {};

  const onCreate = async (form) => {
    try {
      await api.post(`/admin/promoters`, form);
      toast.success("Promoter registered.");
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
  };
  const onSave = async (patch) => {
    try {
      await api.put(`/admin/promoters/${editing.id}`, patch);
      toast.success("Promoter updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const doDelete = async () => {
    await api.delete(`/admin/promoters/${confirm.id}`);
    toast.success("Promoter removed.");
    setConfirm(null);
    load();
  };

  return (
    <div data-testid="admin-promoters-page">
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <Megaphone className="w-5 h-5" /> Field Promoters
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 inline-flex items-center gap-1">
            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button data-testid="admin-promoter-create" onClick={() => setCreating(true)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-[#1B4332] text-white inline-flex items-center gap-1">
            <UserPlus className="w-3.5 h-3.5" /> Register promoter
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Only registered active promoters can access the Promoter dashboard. They see only their own logs and tasks.
      </p>

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Phone</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Visited</th>
              <th className="text-left p-2">Interested</th>
              <th className="text-left p-2">Follow-up</th>
              <th className="text-left p-2">Signed up</th>
              <th className="text-left p-2">Tasks (open / done / late)</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const m = perfFor(p.id);
              return (
                <tr key={p.id} data-testid={`promoter-row-${p.id}`} className="border-t border-slate-100">
                  <td className="p-2 font-medium">{p.name}</td>
                  <td className="p-2">{p.email}</td>
                  <td className="p-2 text-slate-600">{p.phone || "-"}</td>
                  <td className="p-2"><StatusPill status={p.status} /></td>
                  <td className="p-2">{m.visited ?? 0}</td>
                  <td className="p-2 text-emerald-700">{m.interested ?? 0}</td>
                  <td className="p-2 text-amber-700">{m.follow_up_needed ?? 0}</td>
                  <td className="p-2 text-[#1B4332] font-medium">{m.signed_up ?? 0}</td>
                  <td className="p-2 text-slate-600">{(m.tasks_assigned ?? 0)} / {(m.tasks_completed ?? 0)} / {(m.tasks_overdue ?? 0)}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button data-testid={`promoter-edit-${p.id}`} onClick={() => setEditing(p)}
                            className="text-xs px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button data-testid={`promoter-delete-${p.id}`} onClick={() => setConfirm(p)}
                            className="ml-1 text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-slate-500">No promoters yet: register one.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <PromoterFormModal onClose={() => setCreating(false)} onSave={onCreate} title="Register promoter" testId="promoter-create-modal" />}
      {editing && <PromoterFormModal onClose={() => setEditing(null)} onSave={onSave} item={editing} title={`Edit ${editing.name}`} testId="promoter-edit-modal" />}
      {confirm && (
        <Modal title={`Remove ${confirm.name}?`} onClose={() => setConfirm(null)} testId="confirm-delete-promoter">
          <p className="text-sm text-slate-700">This unregisters the promoter and demotes any linked user account back to customer.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
            <button data-testid="confirm-delete-promoter-yes" onClick={doDelete} className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white">Yes, remove</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const c = status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
           : status === "banned" ? "bg-rose-50 text-rose-800 border-rose-200"
           : "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`px-1.5 py-0.5 text-[10px] rounded border uppercase ${c}`}>{status}</span>;
}

function PromoterFormModal({ item, onClose, onSave, title, testId }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || "",
    email: item?.email || "",
    phone: item?.phone || "",
    notes: item?.notes || "",
    status: item?.status || "active",
  });
  const submit = () => {
    const payload = isEdit ? { name: form.name, phone: form.phone, notes: form.notes, status: form.status } : form;
    onSave(payload);
  };
  return (
    <Modal title={title} onClose={onClose} testId={testId}>
      <div className="space-y-3 text-sm">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="promoter-form-name" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })}
                testId="promoter-form-email" />
        {isEdit && <p className="text-[11px] text-slate-500 -mt-2">Email cannot be changed. Create a new promoter if needed.</p>}
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="promoter-form-phone" />
        <FieldSelect label="Status" value={form.status} options={["active", "disabled", "banned"]}
                       onChange={(v) => setForm({ ...form, status: v })} testId="promoter-form-status" />
        <FieldTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="promoter-form-notes" />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="promoter-form-submit" onClick={submit} className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white">
          {isEdit ? "Save" : "Register"}
        </button>
      </div>
    </Modal>
  );
}
